import { useEffect } from 'react';
import { useLoaderData, useLocation, useNavigate, useParams } from 'react-router';
import { paths } from '../../../../../common/constants/pathVariables.js';
import { DialogType } from '../../../../../common/enums.js';
import { useSearchParams } from '../../../../../common/hooks/useSearchParams';
import { getCompanyName } from '../../../../../common/utils/accountUtils.js';
import { toCatalogProductsPerCategory } from '../../../../../common/utils/catalogUtils.js';
import { isDefined } from '../../../../../common/utils/objectUtils.js';
import { CatalogDetails } from '../../../../../components/CatalogDetails/CatalogDetails';
import { CatalogReplace } from '../../../../../components/CatalogReplace/CatalogReplace';
import { LOCATION_TYPE_REPLACE } from '../../../../../components/CatalogReplace/CatalogReplaceUtils.js';
import {
  CompanyInfoContainer,
  CompanyInfoSceneTab,
} from '../../../../../components/CompanyInfoScene/CompanyInfoScene.js';
import { CopyCatalogMultiBusinessDialog } from '../../../../../components/CopyCatalogMultiBusinessDialog/CopyCatalogMultiBusinessDialog';
import { DeleteCatalogDialog } from '../../../../../components/Dialogs/DeleteCatalogDialog';
import { useAuth } from '../../../AuthProvider.js';
import { useOnlineModelsContext } from '../../../onlineModel/onlineModelContextProvider.js';
import type { CatalogCode } from '../../../../../common/constants/pathInterfaces.js';
import type { VirtualCatalogDetailsLoaderData } from '../../../../../common/loaders';
import type { CompanyVirtualCatalogs } from '../../../../../components/CatalogReplace/CatalogReplaceUtils.js';
import type { CatalogSearchResponse } from '../../../../../generated/api/catalogSearchResponse.js';
import type { OnlineModelHeader } from '../../../../../generated/api/onlineModelHeader.js';
import type { AuthenticatedUser } from '../../../authState';

const getCompanyVirtualCatalogs = (
  authenticatedUser?: AuthenticatedUser,
  virtualCatalogsForAllCompanies?: CatalogSearchResponse[],
  currentCatalogCode?: string
): CompanyVirtualCatalogs[] => {
  const companyNames = [
    ...(authenticatedUser?.secondaryAccounts?.map(acc => getCompanyName(authenticatedUser, acc.accountMasterId)) || []),
    authenticatedUser?.companyName,
  ].filter(isDefined);

  const filteredCatalogs = virtualCatalogsForAllCompanies?.filter(c => c.result.catalogCode !== currentCatalogCode);

  return companyNames.map(companyName => ({
    companyName,
    virtualCatalogs:
      filteredCatalogs
        ?.filter(catalog => getCompanyName(authenticatedUser, catalog.result.accountMasterId) === companyName)
        .map(catalog => catalog.result) || [],
  }));
};

export const CompanyInfoCatalogPath = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { catalogCode } = useParams<CatalogCode>();
  const { authenticatedUser } = useAuth();
  const {
    allOnlineModelCodes,
    billingAccounts,
    catalog: currentCatalog,
    catalogHeaders: virtualCatalogsForAllCompanies,
    onlineModelHeadersByCategory,
    onlineModelCodesByCategory,
    secondaryAccounts,
  } = useLoaderData() as VirtualCatalogDetailsLoaderData;
  const { mdmId } = useSearchParams<{ mdmId: string }>();
  const baHeaders = (billingAccounts.searchResults ?? []).map(e => e.result);
  const draftOrPublishedCatalog = currentCatalog?.draft || currentCatalog?.published;

  // Priced headers hydrate in the background. Public loader data is the fallback so the page can render
  // immediately even when the private headers endpoint omits some codes for the current account.
  const { priceAlteredModels, fetchHeadersByCodes, isHeaderCodeResolved } = useOnlineModelsContext();
  const allCodesKey = allOnlineModelCodes.join(',');

  useEffect(() => {
    if (allOnlineModelCodes.length > 0) {
      fetchHeadersByCodes(allOnlineModelCodes);
    }
    // `allOnlineModelCodes` is represented by `allCodesKey` to keep the dependency stable across renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allCodesKey, fetchHeadersByCodes]);

  const pricedHeadersByCode = priceAlteredModels.onlineModelHeadersByCode ?? {};
  const publicHeadersByCode = Object.fromEntries(
    Object.values(onlineModelHeadersByCategory)
      .flat()
      .map(header => [header.onlineModelCode, header])
  );
  const pricedOnlineModelHeaders: Record<string, OnlineModelHeader[]> = Object.fromEntries(
    Object.entries(onlineModelCodesByCategory).map(([category, codes]) => [
      category,
      codes.map(code => pricedHeadersByCode[code] ?? publicHeadersByCode[code]).filter(isDefined),
    ])
  );

  return !draftOrPublishedCatalog ? null : (
    <>
      {state?.type === DialogType.COPY_CATALOG_MULTI_BUSINESS && (
        <CopyCatalogMultiBusinessDialog
          catalog={state.catalog}
          secondaryAccountsInfo={secondaryAccounts}
          accounts={[
            ...(secondaryAccounts || []).map(account => {
              return {
                accountMasterId: account.accountMasterId,
                accountName: getCompanyName(authenticatedUser, account.accountMasterId),
              };
            }),
            {
              accountMasterId: authenticatedUser!.mdmId!,
              accountName: authenticatedUser!.companyName,
            },
          ]}
        />
      )}
      {state?.type === DialogType.DELETE_CATALOG_DIALOG && (
        <DeleteCatalogDialog
          accountMasterId={state!.accountMasterId}
          catalogName={state!.catalogName}
          virtualCatalogCode={state!.virtualCatalogCode}
        />
      )}
      <CompanyInfoContainer tab={CompanyInfoSceneTab.CATALOGS} isSingular={true}>
        {state?.type === LOCATION_TYPE_REPLACE ? (
          <CatalogReplace
            virtualCatalog={currentCatalog}
            catalog={draftOrPublishedCatalog}
            companyName={authenticatedUser?.companyName || ''}
            companyVirtualCatalogs={getCompanyVirtualCatalogs(
              authenticatedUser,
              virtualCatalogsForAllCompanies,
              catalogCode
            )}
          />
        ) : (
          <CatalogDetails
            catalog={draftOrPublishedCatalog}
            virtualCatalog={currentCatalog}
            products={toCatalogProductsPerCategory(
              draftOrPublishedCatalog,
              true,
              pricedOnlineModelHeaders,
              code => !isHeaderCodeResolved(code)
            )}
            productsPublished={
              currentCatalog.published
                ? toCatalogProductsPerCategory(currentCatalog.published, true, pricedOnlineModelHeaders)
                : undefined
            }
            companyName={getCompanyName(authenticatedUser, mdmId)}
            onAddMoreProducts={() => {
              navigate(`${paths.COMPANY_INFO_CATALOG_LIST_PRODUCTS}?mdmId=${mdmId}`, {
                state: {
                  catalog: draftOrPublishedCatalog,
                  virtualCatalog: currentCatalog,
                  catalogProducts: toCatalogProductsPerCategory(
                    draftOrPublishedCatalog,
                    true,
                    pricedOnlineModelHeaders,
                    code => !isHeaderCodeResolved(code)
                  ),
                  mdmId,
                },
              });
            }}
            billingAccounts={baHeaders}
            accountMasterId={mdmId!}
          />
        )}
      </CompanyInfoContainer>
    </>
  );
};
